import type { ConditionGroup, FieldId } from "@unbox/api";
import { Plus, XClose } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { cx } from "@/utils/cx";
import { makeDefaultCondition } from "./condition-fields";
import { ConditionRow } from "./condition-row";

const LOGIC_OPTIONS = [
  { id: "all", label: "All Of" },
  { id: "any", label: "Any Of" },
];

interface ConditionGroupCardProps {
  group: ConditionGroup;
  fieldOptions: { id: FieldId; label: string }[];
  cohortOptions: { id: string; label: string }[];
  onChange: (group: ConditionGroup) => void;
  onRemoveGroup?: () => void;
  dashed?: boolean;
}

export function ConditionGroupCard({
  group,
  fieldOptions,
  cohortOptions,
  onChange,
  onRemoveGroup,
  dashed,
}: ConditionGroupCardProps) {
  return (
    <div
      className={cx(
        "flex flex-col gap-3 rounded-xl bg-unbox-cream-50 p-4",
        dashed ? "border border-dashed border-secondary" : "border border-secondary",
      )}
    >
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <div className="w-fit">
            <Select
              size="sm"
              pill
              popoverClassName="w-fit translate-x-[15px] translate-y-[5px]"
              items={LOGIC_OPTIONS}
              selectedKey={group.logic}
              onSelectionChange={(key) => {
                if (key === "all" || key === "any") onChange({ ...group, logic: key });
              }}
            >
              {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          </div>
          <span className="text-xs text-secondary">following conditions</span>
        </div>
        {onRemoveGroup && (
          <button
            type="button"
            className="p-1 text-quaternary hover:text-primary transition-colors"
            onClick={onRemoveGroup}
            aria-label="Remove group"
          >
            <XClose className="size-4" />
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {group.conditions.map((condition, index) => (
          <ConditionRow
            // biome-ignore lint/suspicious/noArrayIndexKey: conditions have no stable id, index is fine for a locally-edited draft list
            key={index}
            condition={condition}
            fieldOptions={fieldOptions}
            cohortOptions={cohortOptions}
            onChange={(updated) =>
              onChange({
                ...group,
                conditions: group.conditions.map((c, i) => (i === index ? updated : c)),
              })
            }
            onRemove={
              group.conditions.length > 1
                ? () => onChange({ ...group, conditions: group.conditions.filter((_, i) => i !== index) })
                : undefined
            }
          />
        ))}
      </div>

      <Button
        color="link-color"
        size="sm"
        iconLeading={Plus}
        onClick={() => onChange({ ...group, conditions: [...group.conditions, makeDefaultCondition()] })}
      >
        Add another condition
      </Button>
    </div>
  );
}
